import { getAuth } from 'firebase/auth';
import { workspaceHeader } from '../../../lib/workspaces';

/**
 * Client for the email backend contracts:
 *   POST /api/email/send                  → 200 { messageId }
 *   POST /api/email/schedule              → 201 { scheduleId, status: 'Scheduled' }
 *   POST /api/email/broadcast             → 202 { jobId, status, recipientCount } (dup key → 200 { jobId, deduplicated: true })
 *   POST /api/email/broadcast/schedule    → 201 { scheduleId, status: 'Scheduled' }
 *   GET  /api/email/scheduled             → { schedules: [...] }
 *   PUT  /api/email/scheduled/:id/cancel      → { status: 'Cancelled' }
 *   PUT  /api/email/scheduled/:id/reschedule  → { status: 'Scheduled' }
 *
 * Error shape: 4xx/5xx { error, errorCode }.
 * Responses are read tolerantly (both contract shapes and the shapes the
 * current server actually returns) so the UI keeps working while the
 * backend rolls forward.
 */

export type ScheduledEmailStatus =
  | 'Draft' | 'Scheduled' | 'Processing' | 'Sent'
  | 'Partially Sent' | 'Failed' | 'Cancelled';

export interface BroadcastRecipient {
  email: string;
  name?: string;
  applicationId?: string | null;
  applicantData?: any;
}

export interface AttachmentPayload {
  name: string;
  base64Content: string;
  mimeType: string;
}

export class EmailApiError extends Error {
  status: number;
  errorCode?: string;
  constructor(message: string, status: number, errorCode?: string) {
    super(message);
    this.name = 'EmailApiError';
    this.status = status;
    this.errorCode = errorCode;
  }
}

/** One idempotency key per compose session; reuse across retries of the same user action. */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `key-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

async function authHeaders(json = true): Promise<Record<string, string>> {
  const token = await getAuth().currentUser?.getIdToken().catch(() => null);
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...workspaceHeader(),
  };
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(path, { ...init, headers: await authHeaders(true) });
  let data: any = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok) {
    const msg =
      (data && (data.error || data.hint || data.message)) ||
      `Request failed (${res.status})`;
    throw new EmailApiError(String(msg), res.status, data?.errorCode);
  }
  return data as T;
}

/* ── Campaign image assets (PR-11 §C) ──
 * POST /api/email/assets stores an image in the public email-assets
 * namespace; the returned path is made absolute against the portal
 * origin so it resolves for external email recipients. */
export async function uploadEmailAsset(file: File): Promise<{ id: string; url: string }> {
  const base64Data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const out = String(reader.result || '');
      resolve(out.includes(',') ? out.slice(out.indexOf(',') + 1) : out);
    };
    reader.onerror = () => reject(new Error('Could not read the image file'));
    reader.readAsDataURL(file);
  });
  const data = await request<any>('/api/email/assets', {
    method: 'POST',
    body: JSON.stringify({ base64Data, mimeType: file.type, filename: file.name }),
  });
  const path = String(data?.url || '');
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return { id: String(data?.id || ''), url: path.startsWith('http') ? path : `${origin}${path}` };
}

/* ── Individual send ── */

export interface SendIndividualInput {
  to: string;
  toName?: string;
  subject: string;
  htmlBody: string;
  idempotencyKey?: string;
  applicationId?: string;
  sentBy?: string;
  attachments?: AttachmentPayload[];
  bcc?: string;
  senderAddress?: string;
  applicantData?: any;
  /**
   * Document templates to generate from the recipient's application at
   * send time (Slice 3). The server generates each one, saves it to the
   * applicant's Generated Documents, and attaches the PDF. On the
   * schedule contract (ScheduleIndividualInput) the ids are stored and
   * generation happens at execution time instead.
   */
  documentTemplateIds?: string[];
  /**
   * Preview/test sends only (Slice 4, fork F-2): when true, selected
   * documents are generated for the preview applicant and attached, but
   * NOT saved to their Generated Documents. Ignored when no
   * documentTemplateIds are selected. Real sends never set this.
   */
  previewDocumentGeneration?: boolean;
}

export async function sendIndividualEmail(
  input: SendIndividualInput,
): Promise<{ messageId: string }> {
  const data = await request<any>('/api/email/send', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return { messageId: data.messageId || data.id || '' };
}

/* ── Individual schedule ── */

export interface ScheduleIndividualInput extends SendIndividualInput {
  sendAt: string; // UTC ISO
  timezone: string; // display-only; sendAt is persisted as UTC ISO
}

export async function scheduleIndividualEmail(
  input: ScheduleIndividualInput,
): Promise<{ scheduleId: string; status: ScheduledEmailStatus; deduplicated?: boolean }> {
  const data = await request<any>('/api/email/schedule', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return {
    scheduleId: data.scheduleId || data.id || '',
    status: (data.status || 'Scheduled') as ScheduledEmailStatus,
    deduplicated: data.deduplicated,
  };
}

/* ── Broadcast (send now) ── */

export interface BroadcastInput {
  recipients: BroadcastRecipient[];
  subject: string;
  htmlBody: string;
  idempotencyKey: string;
  batchId?: string;
  sentBy?: string;
  senderAddress?: string;
  attachments?: AttachmentPayload[];
  /**
   * Document templates to generate PER RECIPIENT from that recipient's
   * own application when the campaign is sent (Slice 4). The ids ride
   * the bulk job; each generated document is saved to the recipient's
   * Generated Documents and attached to their email only. On the
   * schedule contract (ScheduleBroadcastInput) the ids are stored per
   * recipient and generation happens at execution time. A recipient
   * whose generation fails does NOT receive the email — their job item
   * fails with the generation reason while other recipients proceed.
   */
  documentTemplateIds?: string[];
}

export interface BroadcastResult {
  jobId: string;
  status: string;
  recipientCount: number;
  deduplicated?: boolean;
}

export async function broadcastEmail(input: BroadcastInput): Promise<BroadcastResult> {
  const res = await fetch('/api/email/broadcast', {
    method: 'POST',
    headers: await authHeaders(true),
    body: JSON.stringify(input),
  });
  let data: any = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok && res.status !== 200) {
    const msg = (data && (data.error || data.hint)) || `Broadcast failed (${res.status})`;
    throw new EmailApiError(String(msg), res.status, data?.errorCode);
  }
  return {
    jobId: data.jobId || data.id || '',
    status: data.status || 'queued',
    recipientCount: data.recipientCount ?? data.totalRecipients ?? input.recipients.length,
    deduplicated: data.deduplicated,
  };
}

/* ── Broadcast schedule ── */

export interface ScheduleBroadcastInput extends BroadcastInput {
  sendAt: string; // UTC ISO
  timezone: string; // display-only; sendAt is persisted as UTC ISO
}

export interface ScheduleBroadcastResult {
  scheduleId: string;
  /** ES-6: every schedule id that was persisted (one per recipient). */
  scheduleIds: string[];
  scheduled?: number;
  status: ScheduledEmailStatus;
  /** ES-6: true when only SOME recipients' rows persisted (HTTP 207). */
  partial?: boolean;
  persistedCount?: number;
  failedCount?: number;
  recipientCount?: number;
  batchId?: string;
}

export async function scheduleBroadcast(
  input: ScheduleBroadcastInput,
): Promise<ScheduleBroadcastResult> {
  const data = await request<any>('/api/email/broadcast/schedule', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  const scheduleIds: string[] = Array.isArray(data.scheduleIds)
    ? data.scheduleIds
    : data.scheduleId || data.id
      ? [data.scheduleId || data.id]
      : [];
  return {
    scheduleId: data.scheduleId || data.id || scheduleIds[0] || '',
    scheduleIds,
    scheduled: data.scheduled,
    status: (data.status || 'Scheduled') as ScheduledEmailStatus,
    partial: data.partial === true,
    persistedCount: data.persistedCount,
    failedCount: data.failedCount,
    recipientCount: data.recipientCount,
    batchId: data.batchId,
  };
}

/* ── Scheduled list / cancel / reschedule ── */

export interface ScheduledEmail {
  id: string;
  status: ScheduledEmailStatus;
  sendAt: string;
  scheduledAt?: string;
  processingStartedAt?: string;
  completedAt?: string;
  timezone?: string;
  recipientCount?: number;
  sentCount?: number;
  failedCount?: number;
  /** ES-6: schedule kind — every row carries one, 'batch' included. */
  kind?: 'single' | 'broadcast' | 'document' | 'batch';
  // single-email fields
  to?: string;
  toName?: string;
  subject?: string;
  // campaign fields
  batchId?: string;
  campaign?: boolean;
  applicationId?: string;
}

export async function listScheduledEmails(params?: {
  page?: number;
  pageSize?: number;
  status?: string;
  /** ES-6: filter by schedule kind ('single' | 'broadcast' | 'document' | 'batch'). */
  kind?: string;
}): Promise<{ schedules: ScheduledEmail[]; total?: number }> {
  const qs = new URLSearchParams();
  if (params?.page) qs.set('page', String(params.page));
  if (params?.pageSize) qs.set('pageSize', String(params.pageSize));
  if (params?.status) qs.set('status', String(params.status));
  if (params?.kind) qs.set('kind', String(params.kind));
  const data = await request<any>(
    `/api/email/scheduled${qs.toString() ? `?${qs.toString()}` : ''}`,
    { method: 'GET' },
  );
  return {
    schedules: data.schedules || data.scheduled || [],
    total: data.total,
  };
}

export async function cancelScheduledEmail(id: string): Promise<{ status: ScheduledEmailStatus }> {
  const data = await request<any>(`/api/email/scheduled/${encodeURIComponent(id)}/cancel`, {
    method: 'PUT',
    body: JSON.stringify({}),
  });
  return { status: (data.status || 'Cancelled') as ScheduledEmailStatus };
}

export async function rescheduleScheduledEmail(
  id: string,
  sendAt: string,
  timezone?: string,
): Promise<{ status: ScheduledEmailStatus }> {
  const data = await request<any>(
    `/api/email/scheduled/${encodeURIComponent(id)}/reschedule`,
    {
      method: 'PUT',
      body: JSON.stringify({ sendAt, ...(timezone ? { timezone } : {}) }),
    },
  );
  return { status: (data.status || 'Scheduled') as ScheduledEmailStatus };
}

/* ── Bulk-job progress (broadcast send-now tracking) ── */

export interface EmailJobProgress {
  id: string;
  status: string;
  totalItems: number;
  queuedItems: number;
  processingItems: number;
  completedItems: number;
  failedItems: number;
  skippedItems: number;
  cancelledItems: number;
  progressPercent: number;
}

export interface EmailJobItem {
  id: string;
  recipientEmail?: string;
  status: string;
  /** Human-readable failure reason (the job item's error message). */
  error?: string;
  /** Stable failure code (e.g. 'DOCUMENT_GENERATION_FAILED' when the recipient's documents could not be generated and the email was not sent). */
  errorCode?: string;
  attempts?: number;
}

const ACTIVE_JOB_STATUSES = new Set(['queued', 'processing', 'paused']);

export function isJobActive(status: string): boolean {
  return ACTIVE_JOB_STATUSES.has(String(status || '').toLowerCase());
}

export async function getEmailJob(jobId: string): Promise<EmailJobProgress | null> {
  const data = await request<any>(`/api/admin/bulk-jobs/${encodeURIComponent(jobId)}`, {
    method: 'GET',
  });
  const job = data.job || data;
  if (!job || !job.id) return null;
  return {
    id: job.id,
    status: job.status || 'unknown',
    totalItems: job.totalItems ?? 0,
    queuedItems: job.queuedItems ?? 0,
    processingItems: job.processingItems ?? 0,
    completedItems: job.completedItems ?? 0,
    failedItems: job.failedItems ?? 0,
    skippedItems: job.skippedItems ?? 0,
    cancelledItems: job.cancelledItems ?? 0,
    progressPercent: job.progressPercent ?? 0,
  };
}

export async function getEmailJobItems(
  jobId: string,
  opts?: { status?: string; limit?: number },
): Promise<EmailJobItem[]> {
  const qs = new URLSearchParams();
  if (opts?.status) qs.set('status', opts.status);
  qs.set('limit', String(Math.min(opts?.limit ?? 200, 500)));
  const data = await request<any>(
    `/api/admin/bulk-jobs/${encodeURIComponent(jobId)}/items?${qs.toString()}`,
    { method: 'GET' },
  );
  // The endpoint returns raw job item documents, whose `error` is an
  // OBJECT ({ code, message, retryable }) — normalize it to the message
  // string + code the UI contract declares, so a failed recipient's
  // reason actually renders (an object child would break rendering).
  return (data.items || []).map((item: any) => ({
    id: String(item?.id || ''),
    recipientEmail: item?.recipientEmail,
    status: String(item?.status || ''),
    error:
      typeof item?.error === 'string'
        ? item.error
        : typeof item?.error?.message === 'string'
          ? item.error.message
          : undefined,
    errorCode: typeof item?.error?.code === 'string' ? item.error.code : undefined,
    attempts: typeof item?.attempts === 'number' ? item.attempts : undefined,
  }));
}
